const chapter = {
  id: 'three-d-geometry',
  slug: 'three-d-geometry',
  subject: 'mathematics',
  subjectLabel: 'Mathematics',
  exam: 'JEE Main + Advanced',
  title: {
    english: 'Three-Dimensional Geometry',
        hindi: 'त्रि-आयामी ज्यामिति',
  },
  estReadMinutes: 55,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2–3 questions in JEE Main',
        hindi: 'जेईई मेन में 2-3 प्रश्न',
  },
  syllabusTags: ['Vectors-3D', 'JEE-Main', 'JEE-Advanced', 'Class-12'],
  sections: [
    {
      id: 'basics',
      title: {
        english: 'Coordinates, Direction Cosines & Direction Ratios',
        hindi: 'निर्देशांक, दिक्-कोज्याएँ और दिक्-अनुपात',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'A point $P$ in space is specified by ordered triple $(x, y, z)$ relative to mutually perpendicular axes. Distance: $|PQ| = \\sqrt{(x_2-x_1)^2 + (y_2-y_1)^2 + (z_2-z_1)^2}$. Section formula (internal, ratio $m:n$): $\\left(\\dfrac{m x_2 + n x_1}{m+n}, \\dfrac{m y_2 + n y_1}{m+n}, \\dfrac{m z_2 + n z_1}{m+n}\\right)$.',
        hindi: 'अंतरिक्ष में एक बिंदु $P$ परस्पर लंबवत अक्षों के सापेक्ष क्रमित ट्रिपल $(x, y, z)$ द्वारा निर्दिष्ट किया जाता है। दूरी: $|PQ| = \\sqrt{(x_2-x_1)^2 + (y_2-y_1)^2 + (z_2-z_1)^2}$. अनुभाग सूत्र (आंतरिक, अनुपात $m:n$): $\\left(\\dfrac{m x_2 + n x_1}{m+n}, \\dfrac{m y_2 + n y_1}{m+n}, \\dfrac{m z_2 + n z_1}{m+n}\\right)$।',
          },
        },
        {
          type: 'definition',
          term: {
            english: 'Direction cosines & ratios',
        hindi: 'दिशा कोसाइन और अनुपात',
          },
          body: {
            english: 'If a line makes angles $\\alpha, \\beta, \\gamma$ with positive $x, y, z$ axes, its **direction cosines** (DCs) are $l = \\cos\\alpha,\\ m = \\cos\\beta,\\ n = \\cos\\gamma$ and satisfy $l^2 + m^2 + n^2 = 1$. Any triple $(a, b, c)$ proportional to $(l, m, n)$ is a set of **direction ratios** (DRs); $l : m : n = a : b : c$ and $l = \\dfrac{a}{\\sqrt{a^2+b^2+c^2}}$ etc.',
        hindi: 'यदि कोई रेखा धनात्मक $x, y, z$ अक्षों के साथ $\\alpha, \\beta, \\gamma$ कोण बनाती है, तो इसकी **दिशा कोज्या** (DCs) $l = \\cos\\alpha,\\ m = \\cos\\beta,\\ n = \\cos\\gamma$ हैं और $l^2 + m^2 + n^2 = 1$ को संतुष्ट करती हैं। $(l, m, n)$ के आनुपातिक कोई भी ट्रिपल $(a, b, c)$ **दिशा अनुपात** (DRs) का एक सेट है; $l: m: n = a: b: c$ और $l = \\dfrac{a}{\\sqrt{a^2+b^2+c^2}}$ आदि।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'DRs of line through $P(x_1,y_1,z_1)$ and $Q(x_2,y_2,z_2)$: $(x_2-x_1, y_2-y_1, z_2-z_1)$.',
        hindi: '$P(x_1,y_1,z_1)$ और $Q(x_2,y_2,z_2)$ से होकर गुजरने वाली लाइन के डीआर: $(x_2-x_1, y_2-y_1, z_2-z_1)$।',
            },
            {
              english: `Angle $\\theta$ between two lines with DRs $(a_1,b_1,c_1)$ and $(a_2,b_2,c_2)$:
$\\cos\\theta = \\dfrac{|a_1 a_2 + b_1 b_2 + c_1 c_2|}{\\sqrt{a_1^2+b_1^2+c_1^2}\\sqrt{a_2^2+b_2^2+c_2^2}}$.`,
              hindi: 'DRs $(a_1,b_1,c_1)$ और $(a_2,b_2,c_2)$ वाली दो रेखाओं के बीच का कोण $\\theta$: $\\cos\\theta = \\dfrac{|a_1 a_2 + b_1 b_2 + c_1 c_2|}{\\sqrt{a_1^2+b_1^2+c_1^2}\\sqrt{a_2^2+b_2^2+c_2^2}}$. **बिंदु $\\theta$ के माध्यम से, $(a_1,b_1,c_1)$ के समानांतर**: सदिश: $(a_2,b_2,c_2)$. कार्टेशियन (DRs $\\cos\\theta = \\dfrac{|a_1 a_2 + b_1 b_2 + c_1 c_2|}{\\sqrt{a_1^2+b_1^2+c_1^2}\\sqrt{a_2^2+b_2^2+c_2^2}}$): । **दो बिंदुओं के माध्यम से **: , या ।',
            },
            {
              english: 'Perpendicular: $a_1 a_2 + b_1 b_2 + c_1 c_2 = 0$. Parallel: $\\dfrac{a_1}{a_2} = \\dfrac{b_1}{b_2} = \\dfrac{c_1}{c_2}$.',
        hindi: 'लंबवत: $a_1 a_2 + b_1 b_2 + c_1 c_2 = 0$. समानांतर: $\\dfrac{a_1}{a_2} = \\dfrac{b_1}{b_2} = \\dfrac{c_1}{c_2}$.',
            },
          ],
        },
      ],
    },
    {
      id: 'line',
      title: {
        english: 'Equation of a Line',
        hindi: 'रेखा का समीकरण',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Vector & Cartesian forms',
        hindi: 'सदिश और कार्टेशियन रूप',
          },
          statement: {
            english: `**Through point $\\vec a$, parallel to $\\vec b$**:
Vector: $\\vec r = \\vec a + t\\,\\vec b$.
Cartesian (DRs $a, b, c$): $\\dfrac{x - x_1}{a} = \\dfrac{y - y_1}{b} = \\dfrac{z - z_1}{c}$.

**Through two points $\\vec a, \\vec b$**:
$\\vec r = \\vec a + t(\\vec b - \\vec a)$, or $\\dfrac{x - x_1}{x_2 - x_1} = \\dfrac{y - y_1}{y_2 - y_1} = \\dfrac{z - z_1}{z_2 - z_1}$.`,
            hindi: '**बिंदु $\\vec a$ से होकर, $\\vec b$ के समानांतर**: सदिश: $\\vec r = \\vec a + t\\,\\vec b$. कार्टेशियन (DRs $a, b, c$): $\\dfrac{x - x_1}{a} = \\dfrac{y - y_1}{b} = \\dfrac{z - z_1}{c}$। **दो बिंदुओं के माध्यम से $\\vec a, \\vec b$**: $\\vec r = \\vec a + t(\\vec b - \\vec a)$, या $\\dfrac{x - x_1}{x_2 - x_1} = \\dfrac{y - y_1}{y_2 - y_1} = \\dfrac{z - z_1}{z_2 - z_1}$।',
          },
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'Find the equation of the line through $(1, 2, 3)$ parallel to the vector $2\\hat i - 3\\hat j + \\hat k$.',
        hindi: 'सदिश $2\\hat i - 3\\hat j + \\hat k$ के समानांतर $(1, 2, 3)$ से होकर गुजरने वाली रेखा का समीकरण ज्ञात कीजिए।',
          },
          solution: {
            english: 'Cartesian: $\\dfrac{x-1}{2} = \\dfrac{y-2}{-3} = \\dfrac{z-3}{1}$.',
        hindi: 'कार्तीय: $\\dfrac{x-1}{2} = \\dfrac{y-2}{-3} = \\dfrac{z-3}{1}$.',
          },
        },
      ],
    },
    {
      id: 'angle-distance',
      title: {
        english: 'Angle Between Lines, Skew Lines & Shortest Distance',
        hindi: 'रेखाओं के बीच कोण, विषम रेखाएँ और न्यूनतम दूरी',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Shortest distance — skew lines',
        hindi: 'सबसे छोटी दूरी - तिरछी रेखाएं',
          },
          statement: {
            english: `For $\\vec r_1 = \\vec a_1 + t \\vec b_1$ and $\\vec r_2 = \\vec a_2 + s \\vec b_2$:
$$d = \\dfrac{|(\\vec a_2 - \\vec a_1) \\cdot (\\vec b_1 \\times \\vec b_2)|}{|\\vec b_1 \\times \\vec b_2|}.$$
Lines **intersect** iff $(\\vec a_2 - \\vec a_1) \\cdot (\\vec b_1 \\times \\vec b_2) = 0$ and they're not parallel.`,
            hindi: '$\\vec r_1 = \\vec a_1 + t \\vec b_1$ और $\\vec r_2 = \\vec a_2 + s \\vec b_2$ के लिए: $$d = \\dfrac{|(\\vec a_2 - \\vec a_1) \\cdot (\\vec b_1 \\times \\vec b_2)|}{|\\vec b_1 \\times \\vec b_2|}.$$ रेखाएँ **प्रतिच्छेद** यदि $(\\vec a_2 - \\vec a_1) \\cdot (\\vec b_1 \\times \\vec b_2) = 0$ हैं और वे समानांतर नहीं हैं।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Distance between parallel lines',
        hindi: 'समानांतर रेखाओं के बीच की दूरी',
          },
          statement: {
            english: `For $\\vec r = \\vec a_1 + t \\vec b$ and $\\vec r = \\vec a_2 + s \\vec b$:
$$d = \\dfrac{|(\\vec a_2 - \\vec a_1) \\times \\vec b|}{|\\vec b|}.$$`,
            hindi: '$\\vec r = \\vec a_1 + t \\vec b$ और $\\vec r = \\vec a_2 + s \\vec b$ के लिए: $$d = \\dfrac{|(\\vec a_2 - \\vec a_1) \\times \\vec b|}{|\\vec b|}.$$',
          },
        },
      ],
    },
    {
      id: 'plane',
      title: {
        english: 'Equation of a Plane',
        hindi: 'समतल का समीकरण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Normal form** (unit normal $\\hat n$, distance $p$ from origin): $\\vec r \\cdot \\hat n = p$, or $lx + my + nz = p$.',
        hindi: '**सामान्य रूप** (इकाई सामान्य $\\hat n$, मूल से दूरी $p$): $\\vec r \\cdot \\hat n = p$, या $lx + my + nz = p$।',
            },
            {
              english: '**General form**: $ax + by + cz + d = 0$, with normal $(a, b, c)$.',
        hindi: '**सामान्य फॉर्म**: $ax + by + cz + d = 0$, सामान्य $(a, b, c)$ के साथ।',
            },
            {
              english: '**Through point $\\vec a$ with normal $\\vec n$**: $(\\vec r - \\vec a) \\cdot \\vec n = 0$.',
        hindi: '**सामान्य $\\vec n$ के साथ बिंदु $\\vec a$ के माध्यम से**: $(\\vec r - \\vec a) \\cdot \\vec n = 0$।',
            },
            {
              english: '**Through three points** $\\vec a, \\vec b, \\vec c$ (non-collinear): $(\\vec r - \\vec a) \\cdot [(\\vec b - \\vec a) \\times (\\vec c - \\vec a)] = 0$.',
        hindi: '**तीन बिंदुओं के माध्यम से** $\\vec a, \\vec b, \\vec c$ (गैर-संरेख): $(\\vec r - \\vec a) \\cdot [(\\vec b - \\vec a) \\times (\\vec c - \\vec a)] = 0$।',
            },
            {
              english: '**Intercept form**: $\\dfrac{x}{a} + \\dfrac{y}{b} + \\dfrac{z}{c} = 1$.',
        hindi: '**इंटरसेप्ट फॉर्म**: $\\dfrac{x}{a} + \\dfrac{y}{b} + \\dfrac{z}{c} = 1$.',
            },
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Distance from point to plane',
        hindi: 'बिंदु से समतल तक की दूरी',
          },
          statement: {
            english: `Distance of $P(x_1, y_1, z_1)$ from plane $ax + by + cz + d = 0$:
$$\\delta = \\dfrac{|a x_1 + b y_1 + c z_1 + d|}{\\sqrt{a^2 + b^2 + c^2}}.$$`,
            hindi: 'विमान $ax + by + cz + d = 0$ से $P(x_1, y_1, z_1)$ की दूरी: $$\\delta = \\dfrac{|a x_1 + b y_1 + c z_1 + d|}{\\sqrt{a^2 + b^2 + c^2}}.$$',
          },
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Find the plane through $(1, 0, -1)$ perpendicular to the line $\\dfrac{x-2}{1} = \\dfrac{y+1}{-2} = \\dfrac{z}{3}$.',
        hindi: '$(1, 0, -1)$ से होकर रेखा $\\dfrac{x-2}{1} = \\dfrac{y+1}{-2} = \\dfrac{z}{3}$ के लंबवत समतल का पता लगाएं।',
          },
          solution: {
            english: 'Normal = direction $(1, -2, 3)$. Plane: $1(x-1) - 2(y) + 3(z+1) = 0 \\Rightarrow x - 2y + 3z + 2 = 0$.',
        hindi: 'सामान्य = दिशा $(1, -2, 3)$। समतल: $1(x-1) - 2(y) + 3(z+1) = 0 \\Rightarrow x - 2y + 3z + 2 = 0$.',
          },
        },
      ],
    },
    {
      id: 'angles',
      title: {
        english: 'Angles: Plane–Plane, Line–Plane',
        hindi: 'कोण: समतल-समतल, रेखा-समतल',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Two planes** with normals $\\vec n_1, \\vec n_2$: $\\cos\\theta = \\dfrac{|\\vec n_1 \\cdot \\vec n_2|}{|\\vec n_1||\\vec n_2|}$. Perpendicular ⇔ $\\vec n_1 \\cdot \\vec n_2 = 0$. Parallel ⇔ $\\vec n_1 \\parallel \\vec n_2$.',
        hindi: '**दो विमान** सामान्य $\\vec n_1, \\vec n_2$: $\\cos\\theta = \\dfrac{|\\vec n_1 \\cdot \\vec n_2|}{|\\vec n_1||\\vec n_2|}$ के साथ। लंबवत ⇔ $\\vec n_1 \\cdot \\vec n_2 = 0$. समानांतर ⇔ $\\vec n_1 \\parallel \\vec n_2$.',
            },
            {
              english: '**Line and plane** (line direction $\\vec b$, plane normal $\\vec n$): $\\sin\\theta = \\dfrac{|\\vec b \\cdot \\vec n|}{|\\vec b||\\vec n|}$ — note **sine**, since $\\theta$ is between line and plane (not its normal).',
        hindi: '**रेखा और समतल** (रेखा की दिशा $\\vec b$, समतल सामान्य $\\vec n$): $\\sin\\theta = \\dfrac{|\\vec b \\cdot \\vec n|}{|\\vec b||\\vec n|}$ - नोट **साइन**, क्योंकि $\\theta$ रेखा और समतल के बीच है (यह सामान्य नहीं है)।',
            },
            {
              english: 'Line lies in plane ⇔ $\\vec b \\cdot \\vec n = 0$ **and** any point of the line lies on the plane.',
        hindi: 'रेखा समतल में स्थित है ⇔ $\\vec b \\cdot \\vec n = 0$ **और** रेखा का कोई भी बिंदु समतल पर स्थित है।',
            },
          ],
        },
      ],
    },
    {
      id: 'family',
      title: {
        english: 'Family of Planes & Foot of Perpendicular',
        hindi: 'समतलों का परिवार और लम्बपाद',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: `Family of planes through the line of intersection of $P_1: \\vec r \\cdot \\vec n_1 = d_1$ and $P_2: \\vec r \\cdot \\vec n_2 = d_2$:
$$\\vec r \\cdot \\vec n_1 - d_1 + \\lambda(\\vec r \\cdot \\vec n_2 - d_2) = 0,\\ \\lambda \\in \\mathbb{R}.$$
Use this to fit an additional condition (e.g. passing through a given point).`,
            hindi: '$P_1: \\vec r \\cdot \\vec n_1 = d_1$ और $P_2: \\vec r \\cdot \\vec n_2 = d_2$ के चौराहे की रेखा के माध्यम से विमानों का परिवार: $$\\vec r \\cdot \\vec n_1 - d_1 + \\lambda(\\vec r \\cdot \\vec n_2 - d_2) = 0,\\\\\\\\ \\lambda \\in \\mathbb{R}.$$ किसी अतिरिक्त स्थिति (उदाहरण के लिए किसी दिए गए बिंदु से गुजरना) को फिट करने के लिए इसका उपयोग करें।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Foot of perpendicular & image (point in plane)',
        hindi: 'लंबवत और छवि का पाद (समतल में बिंदु)',
          },
          statement: {
            english: `For point $P(x_1, y_1, z_1)$ and plane $ax + by + cz + d = 0$:
• Foot $F$: $\\dfrac{x_F - x_1}{a} = \\dfrac{y_F - y_1}{b} = \\dfrac{z_F - z_1}{c} = -\\dfrac{a x_1 + b y_1 + c z_1 + d}{a^2 + b^2 + c^2}$.
• Image $P'$: replace the right side by $-2 \\dfrac{a x_1 + b y_1 + c z_1 + d}{a^2+b^2+c^2}$.`,
            hindi: 'बिंदु $P(x_1, y_1, z_1)$ और समतल $ax + by + cz + d = 0$ के लिए: • फ़ुट $F$: $\\dfrac{x_F - x_1}{a} = \\dfrac{y_F - y_1}{b} = \\dfrac{z_F - z_1}{c} = -\\dfrac{a x_1 + b y_1 + c z_1 + d}{a^2 + b^2 + c^2}$. • छवि $P\\\'$: दाईं ओर को $-2 \\dfrac{a x_1 + b y_1 + c z_1 + d}{a^2+b^2+c^2}$ से बदलें।',
          },
        },
        {
          type: 'example',
          number: 3,
          problem: {
            english: 'Find the foot of perpendicular from $(1, 2, 3)$ to the plane $x - 2y + 2z + 5 = 0$.',
        hindi: '$(1, 2, 3)$ से समतल $x - 2y + 2z + 5 = 0$ तक लंबवत का पाद ज्ञात कीजिए।',
          },
          solution: {
            english: '$k = -\\dfrac{1 - 4 + 6 + 5}{1 + 4 + 4} = -\\dfrac{8}{9}$. Foot $= (1, 2, 3) + k(1, -2, 2) = \\left(\\tfrac{1}{9}, \\tfrac{34}{9}, \\tfrac{11}{9}\\right)$.',
        hindi: '$k = -\\dfrac{1 - 4 + 6 + 5}{1 + 4 + 4} = -\\dfrac{8}{9}$. फ़ुट $= (1, 2, 3) + k(1, -2, 2) = \\left(\\tfrac{1}{9}, \\tfrac{34}{9}, \\tfrac{11}{9}\\right)$.',
          },
        },
      ],
    },
    {
      id: 'sphere',
      title: {
        english: 'Sphere (Advanced Add-on)',
        hindi: 'गोला (उन्नत जोड़)',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Sphere centred at $(a, b, c)$ with radius $r$: $(x-a)^2 + (y-b)^2 + (z-c)^2 = r^2$. General form $x^2 + y^2 + z^2 + 2ux + 2vy + 2wz + d = 0$ has centre $(-u, -v, -w)$ and radius $\\sqrt{u^2 + v^2 + w^2 - d}$.',
        hindi: 'गोला $(a, b, c)$ पर केन्द्रित है जिसकी त्रिज्या $r$: $(x-a)^2 + (y-b)^2 + (z-c)^2 = r^2$ है। सामान्य रूप $x^2 + y^2 + z^2 + 2ux + 2vy + 2wz + d = 0$ का केंद्र $(-u, -v, -w)$ और त्रिज्या $\\sqrt{u^2 + v^2 + w^2 - d}$ है।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Plane intersects sphere in a circle iff distance from centre to plane $< r$.',
        hindi: 'यदि केंद्र से समतल $< r$ की दूरी हो तो तल एक वृत्त में गोले को प्रतिच्छेद करता है।',
            },
            {
              english: 'Tangent plane at point $(x_0, y_0, z_0)$ on sphere $x^2+y^2+z^2 = r^2$: $x x_0 + y y_0 + z z_0 = r^2$.',
        hindi: 'गोले $x^2+y^2+z^2 = r^2$ पर बिंदु $(x_0, y_0, z_0)$ पर स्पर्शरेखा तल: $x x_0 + y y_0 + z z_0 = r^2$।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'l^2 + m^2 + n^2 = 1',
      desc: {
        english: 'Direction cosines',
        hindi: 'दिशा कोसाइन',
      },
    },
    {
      latex: '\\vec r = \\vec a + t\\vec b',
      desc: {
        english: 'Line vector form',
        hindi: 'रेखा सदिश रूप',
      },
    },
    {
      latex: '(\\vec r - \\vec a) \\cdot \\vec n = 0',
      desc: {
        english: 'Plane through point',
        hindi: 'बिंदु से होकर समतल',
      },
    },
    {
      latex: '\\delta = \\tfrac{|ax_1+by_1+cz_1+d|}{\\sqrt{a^2+b^2+c^2}}',
      desc: {
        english: 'Point–plane distance',
        hindi: 'बिंदु-समतल दूरी',
      },
    },
    {
      latex: 'd = \\tfrac{|(\\vec a_2-\\vec a_1)\\cdot(\\vec b_1\\times\\vec b_2)|}{|\\vec b_1\\times\\vec b_2|}',
      desc: {
        english: 'Skew lines SD',
        hindi: 'तिरछी रेखाएं SD',
      },
    },
    {
      latex: '\\sin\\theta = \\tfrac{|\\vec b\\cdot\\vec n|}{|\\vec b||\\vec n|}',
      desc: {
        english: 'Line–plane angle',
        hindi: 'रेखा-समतल कोण',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Distance',
      problem: {
        english: 'Distance of point $(2, 3, -5)$ from plane $2x + y - z = 7$.',
        hindi: 'विमान $2x + y - z = 7$ से बिंदु $(2, 3, -5)$ की दूरी।',
      },
      answer: {
        english: '$\\dfrac{|4 + 3 + 5 - 7|}{\\sqrt 6} = \\dfrac{5}{\\sqrt 6}$',
        hindi: '$\\dfrac{|4 + 3 + 5 - 7|}{\\sqrt 6} = \\dfrac{5}{\\sqrt 6}$',
      },
      solution: {
        english: 'Apply point–plane distance formula.',
        hindi: 'बिंदु-समतल दूरी सूत्र लागू करें।',
      },
    },
    {
      year: 2023,
      exam: 'JEE Main',
      tag: 'Skew lines',
      problem: {
        english: 'Shortest distance between $\\dfrac{x-1}{2} = \\dfrac{y-2}{3} = \\dfrac{z-3}{4}$ and $\\dfrac{x-2}{3} = \\dfrac{y-4}{4} = \\dfrac{z-5}{5}$.',
        hindi: '$\\dfrac{x-1}{2} = \\dfrac{y-2}{3} = \\dfrac{z-3}{4}$ और $\\dfrac{x-2}{3} = \\dfrac{y-4}{4} = \\dfrac{z-5}{5}$ के बीच लघुत्तम दूरी ज्ञात करें।',
      },
      answer: {
        english: '$\\dfrac{1}{\\sqrt 6}$',
        hindi: '$\\dfrac{1}{\\sqrt 6}$',
      },
      solution: {
        english: '$\\Delta\\vec a = (1, 2, 2)$, $\\vec b_1 \\times \\vec b_2 = (-1, 2, -1)$, magnitude $\\sqrt 6$. $\\Delta\\vec a \\cdot (\\vec b_1\\times\\vec b_2) = -1 + 4 - 2 = 1$.',
        hindi: '$\\Delta\\vec a = (1, 2, 2)$, $\\vec b_1 \\times \\vec b_2 = (-1, 2, -1)$, परिमाण $\\sqrt 6$। $\\Delta\\vec a \\cdot (\\vec b_1\\times\\vec b_2) = -1 + 4 - 2 = 1$.',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Plane through line',
      problem: {
        english: 'Plane through line of intersection of $x + y + z = 1, 2x + 3y + 4z = 5$ and perpendicular to $x - y + z = 0$.',
        hindi: '$x + y + z = 1, 2x + 3y + 4z = 5$ के प्रतिच्छेदन रेखा से होकर $x - y + z = 0$ के लंबवत समतल।',
      },
      answer: {
        english: '$x + 2z - 2 = 0$ (after simplifying)',
        hindi: '$x + 2z - 2 = 0$ (सरलीकरण के बाद)',
      },
      solution: {
        english: 'Family $(1+2\\lambda)x + (1+3\\lambda)y + (1+4\\lambda)z = 1+5\\lambda$. Perpendicular condition $(1+2\\lambda)(1) + (1+3\\lambda)(-1) + (1+4\\lambda)(1) = 0 \\Rightarrow 1 + 3\\lambda = 0 \\Rightarrow \\lambda = -1/3$. Substitute back.',
        hindi: 'परिवार $(1+2\\lambda)x + (1+3\\lambda)y + (1+4\\lambda)z = 1+5\\lambda$. लंबवत स्थिति $(1+2\\lambda)(1) + (1+3\\lambda)(-1) + (1+4\\lambda)(1) = 0 \\Rightarrow 1 + 3\\lambda = 0 \\Rightarrow \\lambda = -1/3$. वापस प्रतिस्थापित करें.',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Image',
      problem: {
        english: 'Image of $(1, 0, -1)$ in plane $2x - y + z = 5$.',
        hindi: 'समतल $2x - y + z = 5$ में $(1, 0, -1)$ की छवि।',
      },
      answer: {
        english: 'Apply image formula with $k = -2 \\cdot \\dfrac{2 - 0 - 1 - 5}{6} = \\dfrac{4}{3}\\cdot \\ldots$ — yields image coordinates.',
        hindi: '$k = -2 \\cdot \\dfrac{2 - 0 - 1 - 5}{6} = \\dfrac{4}{3}\\cdot \\ldots$ के साथ छवि सूत्र लागू करें - छवि निर्देशांक प्राप्त होते हैं।',
      },
      solution: {
        english: 'Image $= P + 2k \\vec n / |\\vec n|^2$ where $k = -(aP_x+bP_y+cP_z+d)$.',
        hindi: 'छवि $= P + 2k \\vec n / |\\vec n|^2$ जहां $k = -(aP_x+bP_y+cP_z+d)$।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Advanced',
      tag: 'Line in plane',
      problem: {
        english: 'Find $\\lambda$ so that line $\\dfrac{x-1}{1} = \\dfrac{y-2}{2} = \\dfrac{z+3}{\\lambda}$ lies in plane $x - y + z = 0$.',
        hindi: '$\\lambda$ खोजें ताकि रेखा $\\dfrac{x-1}{1} = \\dfrac{y-2}{2} = \\dfrac{z+3}{\\lambda}$ समतल $x - y + z = 0$ में स्थित हो।',
      },
      answer: {
        english: '$\\lambda = 1$',
        hindi: '$\\lambda = 1$',
      },
      solution: {
        english: 'Direction perpendicular to normal: $1 - 2 + \\lambda = 0$. Also $(1, 2, -3)$ on plane: $1 - 2 - 3 = -4 \\ne 0$. So no value works — actual JEE problem variant places point on plane; method is the same.',
        hindi: 'सामान्य से लंबवत दिशा: $1 - 2 + \\lambda = 0$। इसके अलावा विमान पर $(1, 2, -3)$: $1 - 2 - 3 = -4 \\ne 0$। तो कोई मूल्य काम नहीं करता - वास्तविक जेईई समस्या भिन्न स्थान विमान पर इंगित करते हैं; तरीका वही है.',
      },
    },
  ],
  next: {
    slug: 'probability',
    title: 'Probability',
  },
};

export default chapter;
